import React, { useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { cbtAPI } from '../../api';
import { Sidebar } from '../dashboard_component';

const ImportQuestionsPage = () => {
    const { cbt_data_id } = useParams();
    const navigate = useNavigate();
    const [isCollapsed, setIsCollapsed] = useState(false);

    const [selectedFile, setSelectedFile] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [uploadStatus, setUploadStatus] = useState('');
    const [previewData, setPreviewData] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const fileInputRef = useRef(null);

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        const allowedExtensions = ['pdf', 'docx', 'xlsx', 'xls', 'csv'];
        const ext = file.name.split('.').pop().toLowerCase();
        if (allowedExtensions.includes(ext)) {
            setSelectedFile(file);
            setUploadStatus('');
        } else {
            setUploadStatus('Unsupported file type. Use PDF, DOCX, Excel, or CSV.');
            setSelectedFile(null);
            e.target.value = null;
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            setUploadStatus('Please select a file');
            return;
        }
        setIsProcessing(true);
        setUploadStatus('Processing file...');
        try {
            const response = await cbtAPI.uploadQuestionsFile(cbt_data_id, selectedFile);
            if (response.data && response.data.questions) {
                setPreviewData(response.data.questions);
                setShowPreview(true);
                setUploadStatus(`Found ${response.data.questions.length} questions. Review before importing.`);
                toast.success(`File processed: ${response.data.questions.length} questions ready.`);
            } else {
                setUploadStatus('No questions extracted. Check file format.');
            }
        } catch (error) {
            console.error(error);
            setUploadStatus(error.response?.data?.detail || 'Upload failed');
            toast.error('Failed to process file');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleConfirmImport = async () => {
        if (!previewData || previewData.length === 0) {
            toast.error('No questions to import');
            return;
        }
        setIsProcessing(true);
        try {
            const formatted = previewData.map(q => ({
                question: q.question,
                question_type: q.question_type || 'objectives',
                score: q.score || 1,
                sort: q.sort || 1,
                status: 'draft',
                options: (q.options || []).map(opt => ({
                    content: typeof opt === 'string' ? opt : opt.content,
                    status: opt.is_correct || opt.status || false
                })),
                correct_answer: q.correct_answer || ''
            }));
            const response = await cbtAPI.bulkCreateQuestions(cbt_data_id, formatted);
            if (response.success) {
                toast.success(`Imported ${response.data.question_count || formatted.length} questions`);
                navigate(`/cbt_creator/cbt_data?cbt_data_id=${cbt_data_id}`);
            } else {
                toast.error(response.error || 'Import failed');
            }
        } catch (error) {
            toast.error(error.message || 'Import failed');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleDownloadTemplate = async () => {
        try {
            const response = await cbtAPI.downloadExcelTemplate();
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'question_template.xlsx';
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success('Template downloaded');
        } catch (error) {
            toast.error('Download failed');
        }
    };

    return (
        <div className="flex min-h-screen bg-gray-50">
            <Sidebar isCollapsed={isCollapsed} onToggle={() => setIsCollapsed(!isCollapsed)} />
            <div className={`flex-1 transition-all duration-300 pt-16 lg:pt-5 ${isCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
                <div className="max-w-5xl mx-auto px-4 py-8">
                    <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                        <div className="bg-gradient-to-r from-green-600 to-teal-600 p-6 text-white">
                            <h1 className="text-2xl font-bold flex items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                                </svg>
                                Import Questions from File
                            </h1>
                            <p className="text-green-100">Upload a file with your questions – supports Word, PDF, Excel, CSV</p>
                        </div>

                        <div className="p-6 space-y-6">
                            {/* CRITICAL WORD FORMATTING WARNING */}
                            <div className="mb-6 p-4 bg-red-50 rounded-lg border-l-4 border-red-500">
                                <div className="flex items-start gap-3">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                    <div className="flex-1">
                                        <h4 className="font-bold text-red-800 mb-2">⚠️ CRITICAL: Word Document Formatting Rules</h4>
                                        <div className="space-y-2 text-sm text-red-700">
                                            <p className="font-semibold">❌ AVOID Word Auto-Numbering & Auto-Bullets at ALL COSTS!</p>
                                            <p>Word's automatic formatting will break question detection. Follow these rules strictly:</p>
                                            <ul className="list-disc pl-5 space-y-1 mt-2">
                                                <li>✓ <span className="font-mono bg-red-100 px-1">1. Question text</span> - Type numbers manually with a period and space</li>
                                                <li>✓ <span className="font-mono bg-red-100 px-1">A) Option text</span> - Type option letters manually with parenthesis or period</li>
                                                <li>✓ <span className="font-mono bg-red-100 px-1">Answer: B</span> - Always include "Answer:" followed by the correct letter</li>
                                                <li>❌ <strong>NEVER</strong> use Word's built-in numbered list feature</li>
                                                <li>❌ <strong>NEVER</strong> use Word's bullet points for options</li>
                                                <li>❌ <strong>NEVER</strong> let Word auto-format your numbers/bullets</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Word Format Example */}
                            <div className="mb-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                                <h4 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    📝 Correct Word Document Format (Copy This Template)
                                </h4>
                                <div className="bg-white p-4 rounded-lg font-mono text-sm overflow-x-auto">
                                    <pre className="whitespace-pre-wrap">{`1. A computer is an electronic device that takes ______, processes it, and gives output.
A) Result
B) Data
C) Memory
D) Electricity
Answer: B

2. The main task of the computer can be summarized as ______.
A) Copy–Paste–Delete
B) Input–Process–Output
C) Write–Read–Print
D) Save–Load–Store
Answer: B

3. Which of these is not a characteristic of a computer?
A) Speed
B) Diligence
C) Tiredness
D) Accuracy
Answer: C`}</pre>
                                </div>
                                <p className="text-xs text-yellow-700 mt-2">
                                    💡 <strong>Pro Tip:</strong> Use this exact format. Each question should have: number, question text, options (A, B, C, D...), and Answer: line.
                                </p>
                            </div>

                            {/* Supported File Formats */}
                            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                                <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    Supported File Formats
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                    <div className="flex items-center gap-2">
                                        <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-mono">.docx</span>
                                        <span>Microsoft Word (must follow format above)</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-mono">.pdf</span>
                                        <span>PDF documents (same format as Word)</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-mono">.xlsx, .xls</span>
                                        <span>Excel spreadsheets (dynamic options)</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-mono">.csv</span>
                                        <span>CSV files (plain text format)</span>
                                    </div>
                                </div>
                            </div>

                            {/* Excel Template Download */}
                            <div className="mb-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
                                <div className="flex justify-between items-center flex-wrap gap-3">
                                    <div>
                                        <h4 className="font-semibold text-purple-800 mb-1 flex items-center gap-2">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m-6 4h6M4 4h16a2 2 0 012 2v12a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2z" />
                                            </svg>
                                            Need an Excel Template?
                                        </h4>
                                        <p className="text-sm text-purple-700">
                                            Download our Excel template with examples of all supported formats
                                        </p>
                                    </div>
                                    <button
                                        onClick={handleDownloadTemplate}
                                        className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-all duration-200"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                        </svg>
                                        Download Excel Template
                                    </button>
                                </div>
                            </div>

                            {/* File Upload Section */}
                            <div className="mb-4">
                                <label className="block text-gray-700 font-medium mb-2">
                                    Upload Questions File
                                </label>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    accept=".pdf,.docx,.xlsx,.xls,.csv"
                                    onChange={handleFileSelect}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                                    disabled={isProcessing}
                                />
                                {selectedFile && (
                                    <p className="mt-2 text-sm text-gray-600">
                                        Selected: <strong>{selectedFile.name}</strong> ({(selectedFile.size / 1024).toFixed(2)} KB)
                                    </p>
                                )}
                            </div>

                            {uploadStatus && (
                                <div className={`mb-4 p-3 rounded-lg ${uploadStatus.includes('failed') || uploadStatus.includes('error') ? 'bg-red-100 text-red-700 border border-red-200' : 'bg-blue-100 text-blue-700 border border-blue-200'}`}>
                                    {uploadStatus}
                                </div>
                            )}

                            <div className="flex gap-3">
                                <button
                                    onClick={() => navigate(`/cbt_creator/cbt_data?cbt_data_id=${cbt_data_id}`)}
                                    className="px-5 py-2.5 bg-gray-200 text-gray-800 rounded-xl hover:bg-gray-300 transition-colors font-medium"
                                    disabled={isProcessing}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleUpload}
                                    disabled={isProcessing || !selectedFile}
                                    className="px-5 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isProcessing ? 'Processing...' : 'Upload & Preview'}
                                </button>
                            </div>

                            {/* Excel Format Examples (collapsible) */}
                            <div className="mt-6 pt-4 border-t border-gray-200">
                                <details className="text-sm">
                                    <summary className="cursor-pointer text-blue-600 hover:text-blue-800 font-medium">
                                        📊 View Excel Format Examples
                                    </summary>
                                    <div className="mt-3 space-y-4">
                                        <div>
                                            <h5 className="font-semibold text-gray-700 mb-2">Format 1: Dynamic Columns (Recommended)</h5>
                                            <div className="bg-gray-50 p-3 rounded-lg overflow-x-auto">
                                                <pre className="text-xs">{`| question | option_1 | option_2 | option_3 | option_4 | answer |
|----------|----------|----------|----------|----------|--------|
| What is 2+2? | 3 | 4 | 5 | 6 | 2 |
| What is the capital of France? | Berlin | Paris | London | Madrid | 2 |`}</pre>
                                            </div>
                                            <p className="text-xs text-gray-500 mt-1">Answer can be index (1,2,3) or letter (A,B,C)</p>
                                        </div>
                                        <div>
                                            <h5 className="font-semibold text-gray-700 mb-2">Format 2: Fixed Columns</h5>
                                            <div className="bg-gray-50 p-3 rounded-lg overflow-x-auto">
                                                <pre className="text-xs">{`| question | A | B | C | D | answer |
|----------|---|---|---|---|--------|
| What is 2+2? | 3 | 4 | 5 | 6 | B |
| What is the capital of France? | Berlin | Paris | London | Madrid | B |`}</pre>
                                            </div>
                                        </div>
                                        <div>
                                            <h5 className="font-semibold text-gray-700 mb-2">Format 3: JSON Style (Most Flexible)</h5>
                                            <div className="bg-gray-50 p-3 rounded-lg overflow-x-auto">
                                                <pre className="text-xs">{`| question | options | answer |
|----------|---------|--------|
| What is 2+2? | ["3","4","5","6"] | 2 |
| What is the capital of France? | ["Berlin","Paris","London","Madrid"] | 2 |`}</pre>
                                            </div>
                                        </div>
                                    </div>
                                </details>
                            </div>

                            {/* Additional Tips */}
                            <div className="mt-6 pt-4 border-t border-gray-200">
                                <details className="text-sm">
                                    <summary className="cursor-pointer text-green-600 hover:text-green-800 font-medium">
                                        💡 Additional Tips & Best Practices
                                    </summary>
                                    <div className="mt-3 space-y-3">
                                        <div className="p-3 bg-green-50 rounded-lg">
                                            <h5 className="font-semibold text-green-800 mb-2">✅ Do's:</h5>
                                            <ul className="list-disc pl-5 space-y-1 text-sm text-green-700">
                                                <li>Use manual numbering (1., 2., 3.) for questions</li>
                                                <li>Use manual letters (A), B), C. etc.) for options</li>
                                                <li>Include "Answer:" line with the correct option letter</li>
                                                <li>Save Word documents as .docx format</li>
                                                <li>Test with 1–2 questions first before bulk upload</li>
                                            </ul>
                                        </div>
                                        <div className="p-3 bg-red-50 rounded-lg">
                                            <h5 className="font-semibold text-red-800 mb-2">❌ Don'ts:</h5>
                                            <ul className="list-disc pl-5 space-y-1 text-sm text-red-700">
                                                <li><strong>NEVER</strong> use Word's auto-numbering feature</li>
                                                <li><strong>NEVER</strong> use Word's bullet points for options</li>
                                                <li><strong>NEVER</strong> let Word auto-format your lists</li>
                                                <li><strong>NEVER</strong> mix different option formats in one document</li>
                                                <li><strong>NEVER</strong> use special characters in option letters</li>
                                            </ul>
                                        </div>
                                        <div className="p-3 bg-blue-50 rounded-lg">
                                            <h5 className="font-semibold text-blue-800 mb-2">🔧 How to Disable Auto-Numbering in Word:</h5>
                                            <ol className="list-decimal pl-5 space-y-1 text-sm text-blue-700">
                                                <li>Go to File → Options → Proofing</li>
                                                <li>Click "AutoCorrect Options" → "AutoFormat As You Type" tab</li>
                                                <li>Uncheck "Automatic numbered lists"</li>
                                                <li>Uncheck "Automatic bulleted lists"</li>
                                                <li>Click OK to save changes</li>
                                            </ol>
                                        </div>
                                    </div>
                                </details>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Preview Modal (Enhanced like original PreviewQuestionsModal) */}
            {showPreview && previewData && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl max-w-4xl w-full max-h-[85vh] overflow-auto">
                        <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 rounded-t-xl flex justify-between items-center sticky top-0">
                            <h2 className="text-xl font-bold text-white flex items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                Preview Questions ({previewData.length})
                            </h2>
                            <button onClick={() => setShowPreview(false)} className="text-white hover:text-gray-200 text-2xl">✕</button>
                        </div>
                        <div className="p-6 space-y-6">
                            {previewData.map((question, index) => (
                                <div key={index} className="border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow">
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="font-semibold text-lg text-gray-800">Question {index + 1}:</div>
                                        <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                                            {question.question_type || 'Multiple Choice'}
                                        </span>
                                    </div>
                                    <div
                                        className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200"
                                        dangerouslySetInnerHTML={{ __html: question.question }}
                                    />
                                    {question.options && question.options.length > 0 && (
                                        <div className="space-y-3">
                                            <h5 className="font-medium text-gray-700">Options:</h5>
                                            {question.options.map((option, optIndex) => (
                                                <div
                                                    key={optIndex}
                                                    className={`p-3 rounded-lg border ${option.status || option.is_correct ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <span className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium ${option.status || option.is_correct ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-700'}`}>
                                                            {String.fromCharCode(65 + optIndex)}
                                                        </span>
                                                        <span dangerouslySetInnerHTML={{ __html: option.content || option }} />
                                                        {(option.status || option.is_correct) && (
                                                            <span className="ml-auto text-green-600 text-sm font-medium">
                                                                ✓ Correct Answer
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {question.correct_answer && !question.options?.length && (
                                        <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                            <span className="text-sm font-medium text-blue-700">Correct Answer: </span>
                                            <span dangerouslySetInnerHTML={{ __html: question.correct_answer }} />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                        <div className="bg-gray-50 p-6 border-t border-gray-200 flex justify-end gap-3 sticky bottom-0">
                            <button
                                onClick={() => setShowPreview(false)}
                                className="px-5 py-2.5 bg-gray-200 text-gray-800 rounded-xl hover:bg-gray-300 transition-colors font-medium"
                            >
                                Edit / Cancel
                            </button>
                            <button
                                onClick={handleConfirmImport}
                                disabled={isProcessing}
                                className="px-5 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isProcessing ? (
                                    <>
                                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Importing...
                                    </>
                                ) : (
                                    <>
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Confirm & Import All Questions
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ImportQuestionsPage;